import React, { useEffect, useState } from 'react';
import integrationsService from '../../services/integrationsService';
import { apiErrorMessage } from '../../utils/apiError';
import ResyncProgress from './ResyncProgress';
import '../shared/ConfirmModal.css';

// BULK STATUS RESYNC — resync EVERY record of one kind that had a step of the chosen status
// (warning / error / denied / skipped) in the last N days, across ALL hotels and every pipeline
// implementation that has such records. Opens from the Resync button on Pipelines › Logs.
//
// One resync run is started per (implementation, kind) — each run is the same OHIP Adapter resync
// Step Functions machine the single-pipeline ResyncModal starts, so there is no new server
// behaviour, only many at once. The modal polls them all and shows per-hotel progress.
//
// Superadmin only (the button is hidden too, but a hidden button is not authorisation).

const STATUSES = [
    { value: 'warning', label: 'Warning' },
    { value: 'error',   label: 'Error' },
    { value: 'denied',  label: 'Denied' },
    { value: 'skipped', label: 'Skipped' },
];
const KINDS = [
    { value: '', label: 'Any kind' },
    { value: 'reservations', label: 'Reservations' },
    { value: 'profiles',     label: 'Profiles' },
    { value: 'blocks',       label: 'Blocks' },
];

export default function BulkStatusResyncModal({ onClose }) {
    const [status, setStatus] = useState('warning');
    const [type, setType] = useState('');
    const [days, setDays] = useState(7);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);
    const [runs, setRuns] = useState(null);          // null before start; after: [{ templateId, implementationId, hotelId, hotelname, type, runId, records, error? }]

    const start = async () => {
        setError(null);
        setStarting(true);
        try {
            const res = await integrationsService.resyncLogByStatus({ status, days, ...(type ? { type } : {}) });
            const started = res.runs || [];
            setRuns(started);
            setStarting(false);
            if (!started.length) setError(`No ${status} records found in the last ${days} day${days === 1 ? '' : 's'}.`);
        } catch (e) {
            setStarting(false);
            setError(apiErrorMessage(e, 'The status resync could not be started.'));
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog cm-dialog-wide" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
                <h2 className="cm-title">Resync by status across all hotels</h2>

                {!runs ? (
                    <>
                        <p className="cm-message">
                            Re-query OHIP for every record that had the chosen status in the log, in the last N
                            days — across every hotel and every pipeline implementation that has such records.
                            Each implementation runs its own resync; each record is read fresh from OHIP (not a
                            replay of what arrived before) and sent through the pipeline again.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.75rem 1rem' }}>
                                <div style={{ fontWeight: 600, color: '#111827', marginBottom: '0.4rem' }}>What to resync</div>
                                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                                    Every record that had this status in the pipeline log is re-queried from OHIP
                                    and sent through its pipeline again, to live targets.
                                </div>
                                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        Status<br />
                                        <select value={status} onChange={e => { setStatus(e.target.value); setError(null); }}>
                                            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                        </select>
                                    </label>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        Kind<br />
                                        <select value={type} onChange={e => { setType(e.target.value); setError(null); }}>
                                            {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                                        </select>
                                    </label>
                                    <label style={{ fontSize: '0.8rem', color: '#374151' }}>
                                        In the last (days)<br />
                                        <input type="number" min={1} max={90} value={days}
                                            onChange={e => setDays(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 90))} />
                                    </label>
                                </div>
                            </div>
                        </div>

                        {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Cancel</button>
                            <button className="cm-btn-confirm" onClick={start} disabled={starting}>
                                {starting ? 'Starting…' : 'Start resync'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <ResyncProgress runs={runs} noun="status resyncs" />

                        {error && <div className="cm-message" style={{ color: '#dc2626' }}>{error}</div>}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Close</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
